import { z } from 'zod';
import { drivetrainSchema } from './vehicle-taxonomy.js';
import { paginationQuerySchema, paginated } from './common.js';
export const vehicleSortSchema = z.enum(['name', 'registration', 'status', 'createdAt']);
/**
 * Mirrors VEHICLE_STATUSES in @oip/domain and the Vehicle_status_check
 * constraint. MAINTENANCE is accepted but nothing in the engine reads it yet.
 */
export const vehicleStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'MAINTENANCE']);
export const vehicleListQuerySchema = paginationQuerySchema
    .extend({
    sort: vehicleSortSchema.default('name'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    status: vehicleStatusSchema.optional(),
    search: z.string().trim().min(1).optional(),
    /**
     * Archived vehicles are hidden unless explicitly requested. Optional rather
     * than defaulted so the inferred query type stays partial for callers; an
     * absent value is treated as false.
     */
    /*
     * `z.enum` rather than `z.coerce.boolean()` — risk R8. Coercion is
     * truthiness-based, so the STRING "false" arrives truthy and `?includeArchived=false`
     * did the opposite of what it says. Converted with `=== 'true'` in the
     * service rather than a `.transform`, which would make it a ZodEffects and
     * drop the parameter from the published OpenAPI spec.
     */
    includeArchived: z.enum(['true', 'false']).optional(),
})
    .strict();
/**
 * Free-form vehicle attributes.
 *
 * The Vehicle schema has no VIN, make, model, year or type columns — only this
 * JSON bag — so those live here rather than as first-class fields. Reserved
 * keys are rejected outright so that server-owned identifiers can never be
 * smuggled in through the bag and shadow a real column.
 */
const RESERVED_ATTRIBUTE_KEYS = [
    'tenantid',
    'id',
    'vehicleid',
    'deletedat',
    'createdat',
    'updatedat',
];
export const vehicleAttributesSchema = z
    .record(z.unknown())
    .refine((v) => !Object.keys(v).some((k) => RESERVED_ATTRIBUTE_KEYS.includes(k.toLowerCase())), {
    message: 'attributes may not contain server-owned keys such as tenantId or id',
})
    .refine((v) => JSON.stringify(v).length <= 8_192, {
    message: 'attributes payload is too large (8KB limit)',
});
/**
 * Create a vehicle.
 *
 * `tenantId` is absent by construction — it comes from the request context —
 * and `.strict()` turns any attempt to supply it into a 400 rather than a
 * silently ignored field. Immutable server-owned fields (id, createdAt,
 * updatedAt, deletedAt) are likewise unrepresentable.
 */
export const createVehicleRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    registration: z.string().trim().min(1).max(32),
    status: vehicleStatusSchema.default('ACTIVE'),
    attributes: vehicleAttributesSchema.default({}),
    /**
     * The CUSTOMER's own unique identifier for this vehicle.
     *
     * ⚠️ **Deliberately GENERAL**, and narrower wording here was corrected the same day: an
     * asset or fleet number, a serial, or the telematics unit's **IMEI** — whichever the
     * customer keeps. Nothing resolves on it; it exists so a person can find the vehicle
     * their own records name.
     *
     * ⚠️ NOT `providerDeviceId`. That is the PROVIDER's key for the unit and is what
     * telemetry is matched on; this is the customer's, and a fleet may hold both.
     *
     * Unique per fleet account and case-insensitive (`FL-0142` is the same asset as
     * `fl-0142`), NULL-exempt, and RESERVED when the vehicle is archived — D3's rule for the
     * plate, for D3's reason: its incidents and trips still carry the number, so issuing it
     * to another vehicle makes two of them indistinguishable in the customer's vocabulary.
     */
    uniqueId: z.string().trim().min(1).max(64).nullable().optional(),
    /**
     * Usable tank capacity in LITRES.
     *
     * ⚠️ Optional, and its ABSENCE is the ordinary state — every vehicle carries none
     * until somebody types one. What it buys is that a fuel drop OIP derived from the
     * percentage level can be stated in litres; without it the drop is reported in
     * percentage points and the litres are WITHHELD rather than shown as 0, because
     * "0 L lost" on a theft is a confident claim that nothing went.
     *
     * ⚠️ Positive only. A zero capacity is a mistake rather than an unknown, and the
     * database refuses it too — otherwise every derived drop would convert to nought.
     */
    tankCapacityL: z.number().positive().max(5000).nullable().optional(),
    /**
     * The id the telematics PROVIDER keys this vehicle's unit by, in THEIR system.
     *
     * ⚠️ **NOT the IMEI**, and an earlier version of this comment said it was. AjnaView
     * sends its own short numeric id — `49` identified Truck 14 in the fuel-card work — so
     * this is a reference into the provider's records rather than the hardware's identity.
     * An IMEI the customer records belongs in `uniqueId`. Nothing is validated as an IMEI
     * either, for the same reason: a 15-digit check would refuse the one provider this
     * platform is integrated with.
     *
     * ⚠️ Ingestion resolves on it BEFORE the registration, because a plate can be re-issued
     * or mistyped where a provider's key cannot. The column and that preference have existed
     * since the fuel-card work; no API or screen could SET it until now, so it was read on
     * every batch and writable only by SQL — the `RawProviderEvent.payload` shape.
     *
     * Unique per fleet account, NULL-exempt, and RELEASED when the vehicle is archived — a
     * telematics unit is physically transferable, so refitting one is ordinary fleet work.
     */
    providerDeviceId: z.string().trim().min(1).max(64).nullable().optional(),
    /** Cohort for like-for-like comparison. Null or absent leaves it unclassified. */
    vehicleClassId: z.string().uuid().nullable().optional(),
    /**
     * Which drivers'-hours ruleset governs this vehicle (AjnaView Q6).
     *
     * ⚠️ **NULL means INHERIT the tenant's configured limits, never "no limits".** Omitted is
     * unchanged; an explicit null clears the assignment and returns the vehicle to the tenant's
     * own thresholds.
     */
    driversHoursRulesetId: z.string().uuid().nullable().optional(),
    /** How it is powered. Null is UNKNOWN, never an assumed diesel. */
    drivetrain: drivetrainSchema.nullable().optional(),
})
    .strict();
/**
 * Update a vehicle. The safe updateable set is exactly: name, registration,
 * status, attributes.
 *
 * Note on `registration`: it is the key by which inbound provider events are
 * resolved to a vehicle (tenant-scoped). Changing it is permitted because
 * fleets genuinely re-register vehicles, but events still keyed to the old
 * value will stop resolving — which is why the change is audited with an
 * explicit before/after.
 */
export const updateVehicleRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    registration: z.string().trim().min(1).max(32).optional(),
    status: vehicleStatusSchema.optional(),
    attributes: vehicleAttributesSchema.optional(),
    /**
     * The CUSTOMER's own unique identifier for this vehicle.
     *
     * ⚠️ **Deliberately GENERAL**, and narrower wording here was corrected the same day: an
     * asset or fleet number, a serial, or the telematics unit's **IMEI** — whichever the
     * customer keeps. Nothing resolves on it; it exists so a person can find the vehicle
     * their own records name.
     *
     * ⚠️ NOT `providerDeviceId`. That is the PROVIDER's key for the unit and is what
     * telemetry is matched on; this is the customer's, and a fleet may hold both.
     *
     * Unique per fleet account and case-insensitive (`FL-0142` is the same asset as
     * `fl-0142`), NULL-exempt, and RESERVED when the vehicle is archived — D3's rule for the
     * plate, for D3's reason: its incidents and trips still carry the number, so issuing it
     * to another vehicle makes two of them indistinguishable in the customer's vocabulary.
     */
    uniqueId: z.string().trim().min(1).max(64).nullable().optional(),
    /**
     * Usable tank capacity in LITRES.
     *
     * ⚠️ Optional, and its ABSENCE is the ordinary state — every vehicle carries none
     * until somebody types one. What it buys is that a fuel drop OIP derived from the
     * percentage level can be stated in litres; without it the drop is reported in
     * percentage points and the litres are WITHHELD rather than shown as 0, because
     * "0 L lost" on a theft is a confident claim that nothing went.
     *
     * ⚠️ Positive only. A zero capacity is a mistake rather than an unknown, and the
     * database refuses it too — otherwise every derived drop would convert to nought.
     */
    tankCapacityL: z.number().positive().max(5000).nullable().optional(),
    /**
     * The id the telematics PROVIDER keys this vehicle's unit by, in THEIR system.
     *
     * ⚠️ **NOT the IMEI**, and an earlier version of this comment said it was. AjnaView
     * sends its own short numeric id — `49` identified Truck 14 in the fuel-card work — so
     * this is a reference into the provider's records rather than the hardware's identity.
     * An IMEI the customer records belongs in `uniqueId`. Nothing is validated as an IMEI
     * either, for the same reason: a 15-digit check would refuse the one provider this
     * platform is integrated with.
     *
     * ⚠️ Ingestion resolves on it BEFORE the registration, because a plate can be re-issued
     * or mistyped where a provider's key cannot. The column and that preference have existed
     * since the fuel-card work; no API or screen could SET it until now, so it was read on
     * every batch and writable only by SQL — the `RawProviderEvent.payload` shape.
     *
     * Unique per fleet account, NULL-exempt, and RELEASED when the vehicle is archived — a
     * telematics unit is physically transferable, so refitting one is ordinary fleet work.
     */
    providerDeviceId: z.string().trim().min(1).max(64).nullable().optional(),
    /** Explicit null CLEARS the class; omitting the field leaves it alone. */
    vehicleClassId: z.string().uuid().nullable().optional(),
    /**
     * Which drivers'-hours ruleset governs this vehicle (AjnaView Q6).
     *
     * ⚠️ **NULL means INHERIT the tenant's configured limits, never "no limits".** Omitted is
     * unchanged; an explicit null clears the assignment and returns the vehicle to the tenant's
     * own thresholds.
     */
    driversHoursRulesetId: z.string().uuid().nullable().optional(),
    /** How it is powered. Null is UNKNOWN, never an assumed diesel. */
    drivetrain: drivetrainSchema.nullable().optional(),
    /**
     * The depot this vehicle is BASED at. Explicit null clears it.
     *
     * ⚠️ **An assignment, never an inference (D11).** OIP fits no position to a
     * shape, so this cannot be derived from telemetry or from a recent geofence
     * exit — somebody configures it, which is what makes "vehicles based at
     * Leeds" an answer anybody can check.
     *
     * Only a zone classified `DEPOT` is eligible (D12), refused server-side with
     * a reason naming what to do about it.
     */
    homeGeofenceId: z.string().uuid().nullable().optional(),
    /**
     * This vehicle's OWN working week, overriding the organisation's calendar.
     *
     * ⚠️ **Explicit null RESTORES inheritance; omitting it leaves it alone.**
     * Collapsing the two would silently return a weekend vehicle to the fleet's
     * week whenever somebody edited its name — the distinction the AI allowance
     * and the note type already make, for the same reason.
     *
     * ⚠️ **Changing it RETIRES this vehicle's baselines** (design D3): a stored
     * `WORKING` normal was built from the population the old calendar selected,
     * so afterwards a day would be compared against a normal that no longer
     * means the same thing. The vehicle then reads `NO_BASELINE` for about three
     * weeks while fresh ones build, which the screen states BEFORE the save.
     *
     * ⚠️ At least ONE day, refused here and in the domain's own parser: a
     * vehicle with no working days has no normal to compare against, so every
     * observation would be measured against the non-working baseline.
     */
    operatingDays: z.array(z.number().int().min(1).max(7)).min(1).max(7).nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' });
export const vehicleLifecycleRequestSchema = z
    .object({ note: z.string().trim().min(1).max(500).optional() })
    .strict();
export const vehicleListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    status: z.string(),
    archived: z.boolean(),
    /**
     * The cohort this vehicle is compared within. Null is a normal state — a
     * fleet that has not classified itself works unchanged, and only comparison
     * is unavailable.
     */
    vehicleClassId: z.string().uuid().nullable(),
    /** NULL means the vehicle is judged by the tenant's own drivers'-hours limits. */
    driversHoursRulesetId: z.string().uuid().nullable(),
    /** The ruleset's name, for display. NULL when the vehicle inherits. */
    driversHoursRulesetName: z.string().nullable(),
    drivetrain: drivetrainSchema.nullable(),
    /** Denormalised for display, so a list need not fetch the classes separately. */
    vehicleClassName: z.string().nullable(),
    /** The customer's own reference, and the provider's — both null on most fleets. */
    uniqueId: z.string().nullable(),
    tankCapacityL: z.number().nullable(),
    providerDeviceId: z.string().nullable(),
    createdAt: z.string(),
});
export const vehicleListResponseSchema = paginated(vehicleListItemSchema);
export const vehicleDetailSchema = vehicleListItemSchema.extend({
    /**
     * The working week this vehicle is judged against, and where it came from.
     *
     * ⚠️ **`source` is what stops an inherited value looking like a decision
     * nobody made** — the `sources` rule the units card follows. NULL
     * `operatingDays` means the vehicle inherits, and the resolved `workingDays`
     * is what the detector actually used either way.
     */
    operatingDays: z.array(z.number().int().min(1).max(7)).nullable(),
    workingDays: z.array(z.number().int().min(1).max(7)),
    workingDaysSource: z.enum(['VEHICLE', 'ORGANISATION']),
    attributes: z.record(z.unknown()),
    counts: z.object({
        openIncidents: z.number().int(),
        totalIncidents: z.number().int(),
        recommendations: z.number().int(),
        events: z.number().int(),
    }),
    updatedAt: z.string(),
});
// sub-resources reuse the incident / recommendation list items via their own
// queries; events get a dedicated lightweight shape.
export const vehicleEventSortSchema = z.enum(['occurredAt', 'eventType', 'severity']);
export const vehicleEventListQuerySchema = paginationQuerySchema
    .extend({
    sort: vehicleEventSortSchema.default('occurredAt'),
    eventType: z.string().optional(),
    severity: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
})
    .strict();
export const fleetEventListItemSchema = z.object({
    id: z.string().uuid(),
    eventType: z.string(),
    severity: z.string(),
    occurredAt: z.string(),
    receivedAt: z.string(),
    measurements: z.record(z.unknown()),
    location: z.record(z.unknown()).nullable(),
});
export const fleetEventListResponseSchema = paginated(fleetEventListItemSchema);
// ── transfer between sibling sub-tenants (vehicle-transfer design) ──────────
/**
 * Move a vehicle to another sub-tenant of the same organisation.
 *
 * Only the destination is accepted: the source is the tenant the request is
 * already in, and the vehicle is in the path. There is no field for "what to
 * move" because that is not negotiable — see the response for what stays behind.
 */
export const transferVehicleRequestSchema = z.object({ toTenantId: z.string().uuid() }).strict();
export const transferVehicleResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    toTenantId: z.string().uuid(),
    /** Hourly telemetry rollups that moved with it. */
    rollupsMoved: z.number().int(),
    /**
     * True when the vehicle had a class, which does not survive the move: a class
     * is a cohort key owned by the sub-tenant it came from.
     */
    vehicleClassCleared: z.boolean(),
    /**
     * Always true, and stated rather than implied.
     *
     * Incidents, fleet events, recommendations and raw telemetry stay with the
     * sub-tenant they were raised in. They carry driver attribution, and drivers
     * do not move — carrying that history across would hand the destination a name
     * and a licence number belonging to another tenant.
     */
    historyLeftBehind: z.literal(true),
});
// ── unresolved inbound references ──────────────────────────────────────────
/**
 * A vehicle a provider keeps reporting that nobody here has created.
 *
 * Ingestion deliberately never creates one: a typo'd registration would become a
 * phantom vehicle, and an archived registration stays RESERVED so an auto-create
 * would collide with a vehicle this tenant cannot see. The refusal is right; it
 * was just silent. This is the list that makes it actionable.
 */
export const unresolvedVehicleSchema = z.object({
    id: z.string().uuid(),
    /** Upper-cased, as the provider sent it. */
    registration: z.string(),
    name: z.string().nullable(),
    providerVehicleId: z.string().nullable(),
    /** EVENT | TELEMETRY — which ingestion path refused it. Both refuse alike. */
    source: z.enum(['EVENT', 'TELEMETRY']),
    /** Which integration it arrived through, so a depot can be identified. */
    providerId: z.string(),
    /** How many times it has been refused. The reason for aggregating. */
    occurrences: z.number().int(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
export const unresolvedVehiclesResponseSchema = z.object({
    items: z.array(unresolvedVehicleSchema),
});
//# sourceMappingURL=vehicle.js.map